/**
 * Ort eines Termins — Standard, oder einer der hinterlegten Orte.
 *
 * Benjamin, 25.09.2026: „Im Termin die Möglichkeit, alternativ einen Ort
 * einzutragen. Dieser Ort muss aus den hinterlegten Orten kommen, die zum
 * Kunden oder zu Lieferanten gehören." Standard bleibt die Baustelle bzw. die
 * Lieferadresse des Kunden; geändert wird nur, wenn man es will.
 *
 * Gewählt wird eine Herkunft (`projekt`, `kunde:liefer`, `kunde:rechnung`,
 * `lieferant:<id>`), keine Adresse: die löst der Server beim Speichern selbst
 * auf (`termin_ort.py`). Fehlt einem Lieferanten die Adresse, wird sie hier
 * gleich am Lieferanten nachgetragen — und steht danach für jeden Termin da.
 */
import { useMemo, useState } from "react";

import { useLieferantAdresse, useTerminOrte } from "../../hooks/usePlanung";
import { errorText } from "../../lib/invoices";
import type { TerminOrtOption } from "../../lib/types";
import StrassenFeld from "../kunden/StrassenFeld";
import { OrtLink } from "./TerminTeile";
import { fieldCap } from "./planer-styles";
import "../../styles/terminort.css";

/** So viele weitere Lieferanten stehen ohne Suche in der Liste. */
const OHNE_SUCHE = 6;

type Props = {
  projektId: string;
  kundeId: string;
  /** Gewählte Herkunft; "" = Standard. */
  quelle: string;
  onQuelle: (quelle: string) => void;
  /** Stand am Termin, wie gespeichert. */
  gespeichert: { quelle: string; ort: string; name: string; standard: string };
  /** Eingeteilte Helfer — sie bekommen beim Ortswechsel einen Hinweis. */
  helfer: number;
};

export function TerminOrtWahl({ projektId, kundeId, quelle, onQuelle, gespeichert, helfer }: Props) {
  const [offen, setOffen] = useState(false);
  const geaendert = quelle !== gespeichert.quelle;
  const orte = useTerminOrte(projektId, kundeId, offen || geaendert);
  const optionen = orte.data?.orte ?? [];
  const gewaehlt = optionen.find((o) => o.quelle === quelle);

  // Was gerade gilt: ungespeicherte Wahl, sonst der Stand am Termin.
  const standard = orte.data?.standard ?? gespeichert.standard;
  const standardName = orte.data?.standard_name ?? "";
  const anzeige = !geaendert
    ? { ort: gespeichert.ort, name: gespeichert.quelle ? gespeichert.name : "" }
    : quelle
      ? { ort: gewaehlt?.adresse ?? "", name: gewaehlt?.name ?? "" }
      : { ort: standard, name: "" };

  const waehlen = (q: string) => { onQuelle(q); setOffen(false); };

  return (
    <div className="to-wahl">
      <span style={fieldCap}>EINSATZORT</span>
      <div className="to-zeile">
        <div className="to-ort">
          {anzeige.ort ? <OrtLink ort={anzeige.ort} /> : <span className="to-leer">Kein Ort hinterlegt</span>}
          <span className="to-herkunft">
            {anzeige.name ? <>Abweichend · {anzeige.name}</> : <>Standard{standardName ? ` · ${standardName}` : ""}</>}
          </span>
        </div>
        <div className="to-knoepfe">
          {quelle && (
            <button type="button" className="to-knopf" onClick={() => waehlen("")} title="Wieder den Standard-Ort nehmen">
              Standard
            </button>
          )}
          <button type="button" className="to-knopf" aria-expanded={offen} onClick={() => setOffen((v) => !v)}>
            {offen ? "Schließen" : "Ort ändern"}
          </button>
        </div>
      </div>
      {geaendert && (
        <div className="to-hinweis">
          Wird beim Speichern übernommen{helfer > 0 ? ` — ${helfer === 1 ? "der Eingeteilte bekommt" : `die ${helfer} Eingeteilten bekommen`} einen Hinweis mit dem neuen Ort` : ""}.
        </div>
      )}
      {offen && (
        <OrtListe
          laedt={orte.isPending}
          fehler={orte.isError ? errorText(orte.error) : ""}
          optionen={optionen}
          standard={standard}
          standardName={standardName}
          quelle={quelle}
          onWahl={waehlen}
        />
      )}
    </div>
  );
}

function OrtListe({ laedt, fehler, optionen, standard, standardName, quelle, onWahl }: {
  laedt: boolean; fehler: string; optionen: TerminOrtOption[]; standard: string; standardName: string;
  quelle: string; onWahl: (quelle: string) => void;
}) {
  const [suche, setSuche] = useState("");
  const [nachtragen, setNachtragen] = useState<string | null>(null);

  const eigene = optionen.filter((o) => o.art !== "lieferant" && !o.ist_standard);
  const { amProjekt, weitere, verborgen } = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const lief = optionen.filter((o) => o.art === "lieferant");
    const passt = (o: TerminOrtOption) => !s || o.name.toLowerCase().includes(s) || o.adresse.toLowerCase().includes(s);
    const am = lief.filter((o) => o.am_projekt && passt(o));
    const rest = lief.filter((o) => !o.am_projekt && passt(o));
    // Ohne Suche nur die mit Adresse und nur ein paar — 86 Lieferanten sind
    // keine Liste, durch die man auf dem Telefon scrollt.
    const sichtbar = s ? rest : rest.filter((o) => o.adresse).slice(0, OHNE_SUCHE);
    return { amProjekt: am, weitere: sichtbar, verborgen: rest.length - sichtbar.length };
  }, [optionen, suche]);

  if (laedt) return <div className="to-liste"><div className="to-leer">Orte werden geladen …</div></div>;
  if (fehler) return <div className="to-liste"><div className="to-fehler">{fehler}</div></div>;

  const zeile = (o: TerminOrtOption) => (
    o.adresse ? (
      <button key={o.quelle} type="button" className={`to-option${quelle === o.quelle ? " is-aktiv" : ""}`} onClick={() => onWahl(o.quelle)}>
        <span className="to-option-name">{o.name}</span>
        <span className="to-option-adresse">{o.adresse}</span>
      </button>
    ) : nachtragen === o.quelle ? (
      <AdresseNachtragen key={o.quelle} option={o} onFertig={() => { setNachtragen(null); onWahl(o.quelle); }} onAbbruch={() => setNachtragen(null)} />
    ) : (
      <button key={o.quelle} type="button" className="to-option is-ohne" onClick={() => setNachtragen(o.quelle)}>
        <span className="to-option-name">{o.name}</span>
        <span className="to-option-adresse">Keine Adresse hinterlegt — eintragen</span>
      </button>
    )
  );

  return (
    <div className="to-liste">
      <button type="button" className={`to-option${quelle === "" ? " is-aktiv" : ""}`} onClick={() => onWahl("")}>
        <span className="to-option-name">Standard{standardName ? ` · ${standardName}` : ""}</span>
        <span className="to-option-adresse">{standard || "Kein Ort hinterlegt"}</span>
      </button>
      {eigene.length > 0 && <div className="to-gruppe">Kunde &amp; Projekt</div>}
      {eigene.map(zeile)}
      <div className="to-gruppe">Lieferanten</div>
      <input
        className="to-suche"
        type="search"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        placeholder="Lieferant suchen …"
        aria-label="Lieferant suchen"
      />
      {amProjekt.length > 0 && <div className="to-untergruppe">Am Projekt</div>}
      {amProjekt.map(zeile)}
      {amProjekt.length > 0 && weitere.length > 0 && <div className="to-untergruppe">Weitere</div>}
      {weitere.map(zeile)}
      {verborgen > 0 && !suche.trim() && (
        <div className="to-leer">{verborgen} weitere — über die Suche finden, auch die ohne Adresse.</div>
      )}
      {suche.trim() && amProjekt.length + weitere.length === 0 && (
        <div className="to-leer">Kein Lieferant „{suche.trim()}“.</div>
      )}
    </div>
  );
}

/** Adresse am Lieferanten nachtragen — gespeichert wird sie sofort, am Lieferanten. */
function AdresseNachtragen({ option, onFertig, onAbbruch }: { option: TerminOrtOption; onFertig: () => void; onAbbruch: () => void }) {
  const speichern = useLieferantAdresse();
  const [strasse, setStrasse] = useState("");
  const [plz, setPlz] = useState("");
  const [ort, setOrt] = useState("");
  const zeile = [strasse.trim(), [plz.trim(), ort.trim()].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const fertig = !!strasse.trim() && !!(plz.trim() || ort.trim());
  return (
    <div className="to-nachtragen">
      <div className="to-option-name">{option.name}</div>
      <StrassenFeld
        value={strasse}
        onChange={setStrasse}
        onPick={(t) => { setStrasse(t.strasse); if (t.plz) setPlz(t.plz); if (t.ort) setOrt(t.ort); }}
        inputClassName="to-feld"
      />
      <div className="to-plzort">
        <input className="to-feld to-plz" value={plz} onChange={(e) => setPlz(e.target.value)} placeholder="PLZ" inputMode="numeric" aria-label="PLZ" />
        <input className="to-feld" value={ort} onChange={(e) => setOrt(e.target.value)} placeholder="Ort" aria-label="Ort" />
      </div>
      <div className="to-klein">Wird am Lieferanten gespeichert und steht danach bei jedem Termin zur Wahl.</div>
      {speichern.isError && <div className="to-fehler">{errorText(speichern.error)}</div>}
      <div className="to-knoepfe">
        <button type="button" className="to-knopf" onClick={onAbbruch}>Abbrechen</button>
        <button
          type="button"
          className="to-knopf is-primaer"
          disabled={!fertig || speichern.isPending}
          onClick={() => option.lieferant_id && speichern.mutate({ id: option.lieferant_id, adresse: zeile }, { onSuccess: onFertig })}
        >
          {speichern.isPending ? "Speichert …" : "Speichern & wählen"}
        </button>
      </div>
    </div>
  );
}
